import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";

// Supplier Performance Report DTO
export interface SupplierPerformanceReportDto {
  // Supplier Information
  supplierId?: number;
  supplierCode?: string;
  supplierName?: string;
  location?: string;
  contactNo?: string;
  email?: string;
  
  // Purchase Order Metrics
  totalPoCount?: number;
  totalPoValue?: number;
  averagePoValue?: number;
  firstPoDate?: string; // LocalDate
  lastPoDate?: string; // LocalDate
  
  // GRN Metrics
  totalGrnCount?: number;
  totalReceivedValue?: number;
  totalReceivedQuantity?: number;
  firstGrnDate?: string; // LocalDate
  lastGrnDate?: string; // LocalDate
  
  // Fulfillment Rate
  fulfillmentRate?: number; // Percentage
  
  // On-time Delivery Metrics
  onTimeDeliveryCount?: number;
  onTimeDeliveryPercentage?: number;
  lateDeliveryCount?: number;
  
  // Average Delivery Time
  averageDeliveryTime?: number; // Days
  
  // Quality Metrics
  totalRejectionQuantity?: number;
  rejectionCount?: number;
  rejectionRatePercentage?: number; // Quantity-based
  rejectionValuePercentage?: number; // Value-based
  
  // Performance Score
  performanceScore?: number; // 0-100
  
  // Delivery Rating
  deliveryRating?: number;
}

// Supplier Balance Report DTO (from Object[] response)
export interface SupplierBalanceReportDto {
  branchId: number;
  poNumber: string;
  rawItemName: string;
  rawItemCategoryName: string;
  sku: string;
  supplierName: string;
  poQty: number;
  receiveQty: number;
}

// Supplier Performance Monthly Rating DTO
export interface SupplierMonthlyRatingDto {
  year: number;
  month: number;
  monthName?: string;
  deliveryRating: number;
}

// Supplier Performance Detail DTO (PO/GRN details) - matches actual API response
export interface SupplierPerformanceDetailDto {
  supplierId: number;
  supplierCode?: string;
  supplierName: string;
  supplierLocation?: string;
  supplierEmail?: string;
  supplierContact?: string;
  supplierIsActive?: boolean;
  branchId: number;
  receiveYear?: number;
  receiveMonth?: number;
  receiveYearMonth?: string;
  receiveMonthName?: string;
  poId: number;
  poNumber: string;
  purchaseDate?: string;
  expectedDeliveryDate?: string | null;
  actualDeliveryDate?: string | null;
  poStatus?: string;
  poType?: string;
  priority?: string;
  currency?: string;
  exchangeRate?: number | null;
  poTotalAmount?: number;
  poGrandTotal?: number;
  poGrandTotalTax?: number;
  transportationCharges?: number;
  insuranceCharges?: number;
  packingCharges?: number;
  installAndServiceCharges?: number;
  itemCostOthCharges?: number;
  paymentTerms?: string;
  deliveryTerms?: string;
  modeOfDespatch?: string;
  poIsActive?: boolean;
  poCreatedAt?: string;
  poApprovedDate?: string;
  grnId?: number;
  grnNumber?: string;
  grnDate?: string;
  receiveDate?: string;
  grnStatus?: string;
  grnType?: string;
  receivedBy?: string | null;
  vehicleNumber?: string | null;
  driverName?: string | null;
  dcNo?: string;
  dcDate?: string | null;
  grnTotalQuantity?: number;
  grnTotalAmount?: number;
  grnNotes?: string | null;
  totalReceivedQuantity?: number;
  totalReceivedAmount?: number;
  uniqueItemsReceived?: number;
  deliveryDelayDays?: number | null;
  deliveryStatus?: string | null;
  fulfillmentPercentage?: number;
  // Legacy fields for backward compatibility
  grnCount?: number;
  grnNumbers?: string; // Comma-separated list
  firstGrnDate?: string;
  lastGrnDate?: string;
  firstReceiveDate?: string;
  lastReceiveDate?: string;
}

// Supplier Performance Details Response
export interface SupplierPerformanceDetailsResponse {
  supplierId: number;
  supplierName: string;
  monthlyRatings: SupplierMonthlyRatingDto[];
  poGrnDetails: SupplierPerformanceDetailDto[];
}

// Supplier Report API Service
class SupplierReportApiService extends BaseApiService {
  
  // Get supplier performance report by branch, year, month, or date range
  async getSupplierPerformanceReport(
    branchId?: number, 
    year?: number, 
    month?: number,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<SupplierPerformanceReportDto[]>> {
    try {
      // Backend returns List directly, not wrapped in ApiResponse
      const data = await this.makeRequest<SupplierPerformanceReportDto[]>(
        API_ENDPOINTS.SUPPLIER_REPORTS.PERFORMANCE(branchId, year, month, startDate, endDate),
        { method: HTTP_METHODS.GET }
      );
      
      // Wrap in ApiResponse format for consistency
      return {
        success: true,
        message: 'Supplier performance report fetched successfully',
        data: Array.isArray(data) ? data : []
      };
    } catch (error) {
      // console.error('SupplierReportApiService: Error fetching supplier performance report:', error);
      toast.error('Failed to fetch supplier performance report');
      throw error;
    }
  }

  // Get supplier balance report by branch
  async getSupplierBalanceReportByBranch(branchId: number): Promise<ApiResponse<SupplierBalanceReportDto[]>> {
    try {
      // Backend returns List<Object[]> directly
      const data = await this.makeRequest<any[]>(
        API_ENDPOINTS.SUPPLIER_REPORTS.SUPPLIER_BALANCE(branchId),
        { method: HTTP_METHODS.GET }
      );
      
      // Transform Object[] to SupplierBalanceReportDto[]
      // Columns in order: branch_id, po_number, raw_item_name, raw_item_category_name, sku, supplier_name, po_qty, receive_qty
      const transformedData: SupplierBalanceReportDto[] = Array.isArray(data) 
        ? data.map((item: any[]) => ({
            branchId: item[0] || 0,
            poNumber: item[1] || '',
            rawItemName: item[2] || '',
            rawItemCategoryName: item[3] || '',
            sku: item[4] || '',
            supplierName: item[5] || '',
            poQty: item[6] || 0,
            receiveQty: item[7] || 0,
          }))
        : [];
      
      return {
        success: true,
        message: 'Supplier balance report fetched successfully',
        data: transformedData
      };
    } catch (error) {
      // console.error('SupplierReportApiService: Error fetching supplier balance report:', error);
      toast.error('Failed to fetch supplier balance report');
      throw error;
    }
  }

  // Get supplier performance details
  async getSupplierPerformanceDetails(
    supplierId: number,
    branchId?: number,
    year?: number,
    month?: number,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<SupplierPerformanceDetailsResponse>> {
    try {
      // Backend returns array of SupplierPerformanceDetailDto directly
      const data = await this.makeRequest<SupplierPerformanceDetailDto[]>(
        API_ENDPOINTS.SUPPLIER_REPORTS.DETAILS(supplierId, branchId, year, month),
        { method: HTTP_METHODS.GET }
      );
      
      // Transform the response to match SupplierPerformanceDetailsResponse
      const supplierName = data && data.length > 0 ? data[0].supplierName : '';
      const supplierIdFromData = data && data.length > 0 ? data[0].supplierId : supplierId;
      
      const response: SupplierPerformanceDetailsResponse = {
        supplierId: supplierIdFromData,
        supplierName: supplierName,
        monthlyRatings: [], // Will be loaded separately
        poGrnDetails: Array.isArray(data) ? data : []
      };
      
      return {
        success: true,
        message: 'Supplier performance details fetched successfully',
        data: response
      };
    } catch (error) {
      // console.error('SupplierReportApiService: Error fetching supplier performance details:', error);
      toast.error('Failed to fetch supplier performance details');
      throw error;
    }
  }

  /** Last 12 months delivery ratings for chart on supplier performance details. */
  async getSupplierPerformanceLast12Months(
    branchId: number,
    supplierId: number
  ): Promise<ApiResponse<SupplierMonthlyRatingDto[]>> {
    try {
      const data = await this.makeRequest<unknown>(
        API_ENDPOINTS.SUPPLIER_REPORTS.LAST_12_MONTHS(branchId, supplierId),
        { method: HTTP_METHODS.GET }
      );

      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
      ];

      const rows = Array.isArray(data) ? data : [];
      const ratings: SupplierMonthlyRatingDto[] = [];

      for (const item of rows) {
        if (item == null || typeof item !== 'object') continue;
        const row = item as Record<string, unknown>;
        const rowSupplierId = row.supplierId;
        if (rowSupplierId != null && Number(rowSupplierId) !== supplierId) continue;

        const year = Number(row.year);
        const month = Number(row.month);
        if (!year || !month) continue;

        ratings.push({
          year,
          month,
          monthName:
            typeof row.monthName === 'string' && row.monthName.trim()
              ? row.monthName
              : `${monthNames[month - 1]} ${year}`,
          deliveryRating: Number(row.deliveryRating ?? 0) || 0,
        });
      }

      ratings.sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month));

      return {
        success: true,
        message: 'Supplier performance last 12 months fetched successfully',
        data: ratings,
      };
    } catch (error) {
      toast.error('Failed to fetch supplier performance history');
      throw error;
    }
  }
}

export const supplierReportApiService = new SupplierReportApiService();

